import { Dropdown, Form, Grid, InputOnChangeData, Item, Label, Segment, Tab } from "semantic-ui-react";
import ErrorsView from "./ErrorsView";
import { FormError } from "../models/FormError";
import { User } from "../models/User";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import { OrgUserFileInput } from "../models/globalTypes";
import OrgUserFilesList from "./OrgUserFilesList";
import { ActiveLicence } from "../models/ActiveLicence";
import { AdminRole } from "../models/AdminRole";

export interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  adminRoleId: string | null;
  phoneNumber: string;
  CatSerialNumber: string;
  CatCalibrationDate: Date;
  CatCalibrationDueDate: Date;
  GennySerialNumber: string;
  GennyCalibrationDate: Date;
  GennyCalibrationDueDate: Date;
  licenceId: string | null;
}

interface Props {
  user?: User;
  formData: FormData;
  formErrors: FormError;
  errors: string[] | undefined;
  onChange: (formData: FormData) => void;
  onEmailEntered: (email: string) => void;
  onAddOrgUserFile: () => void;
  onDeleteOrgUserFile: (orgUserFile: OrgUserFileInput) => void;
  orgUserFiles: OrgUserFileInput[];
  activeLicences?: ActiveLicence[] | null;
  licenceSelectorDisabled?: boolean;
  adminRoles?: AdminRole[];
  adminRoleSelectorDisabled?: boolean;
}

const OrgUserAddForm = ({
  user,
  formData,
  onChange,
  formErrors,
  errors,
  onEmailEntered,
  onAddOrgUserFile,
  onDeleteOrgUserFile,
  orgUserFiles,
  activeLicences,
  licenceSelectorDisabled = false,
  adminRoles,
  adminRoleSelectorDisabled = false,
}: Props) => {
  const handleChange = (e: any, { name, value }: InputOnChangeData) => {
    onChange({ ...formData, [name]: value });
  };

  const handleEmailBlur = (e: any) => {
    onEmailEntered(formData.email);
  };

  const onCatCalibrationDateChange = (e: any, data: any) => {
    formData.CatCalibrationDate = data.value;
  };

  const onGennyCalibrationDateChange = (e: any, data: any) => {
    formData.GennyCalibrationDate = data.value;
  };

  const onCatCalibrationDueDateChange = (e: any, data: any) => {
    formData.CatCalibrationDueDate = data.value;
  };

  const onGennyCalibrationDueDateChange = (e: any, data: any) => {
    formData.GennyCalibrationDueDate = data.value;
  };

  const userInfoItem = (header: string, value?: string | null) => {
    return (
      <Item.Group>
        <Item>
          <Item.Content>
            <Item.Meta>{header}</Item.Meta>
            <Item.Description>{value ? value : ""}</Item.Description>
          </Item.Content>
        </Item>
      </Item.Group>
    );
  };

  const panes = [
    {
      menuItem: "CAT and Genny",
      render: () => (
        <Tab.Pane>
          <Form>
            <Form.Group widths="equal">
              <Form.Input
                label="CAT serial number"
                name="CatSerialNumber"
                value={formData.CatSerialNumber}
                onChange={handleChange}
              />
              <SemanticDatepicker
                label="CAT calibration date"
                value={formData.CatCalibrationDate}
                locale="en-US"
                onChange={onCatCalibrationDateChange}
                type="basic"
              />
              <SemanticDatepicker
                label="CAT calibration due date"
                value={formData.CatCalibrationDueDate}
                locale="en-US"
                onChange={onCatCalibrationDueDateChange}
                type="basic"
              />
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Input
                label="Genny serial number"
                name="GennySerialNumber"
                value={formData.GennySerialNumber}
                onChange={handleChange}
              />
              <SemanticDatepicker
                label="Genny calibration date"
                value={formData.GennyCalibrationDate}
                locale="en-US"
                onChange={onGennyCalibrationDateChange}
                type="basic"
              />
              <SemanticDatepicker
                label="Genny calibration due date"
                value={formData.GennyCalibrationDueDate}
                locale="en-US"
                onChange={onGennyCalibrationDueDateChange}
                type="basic"
              />
            </Form.Group>
          </Form>
        </Tab.Pane>
      ),
    },
    {
      menuItem: "Files",
      render: () => (
        <Tab.Pane>
          <div style={{ minHeight: "163px" }}>
            <OrgUserFilesList
              orgUserFiles={orgUserFiles}
              onAddOrgUserFile={onAddOrgUserFile}
              onDeleteOrgUserFile={onDeleteOrgUserFile}
            />
          </div>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      <Form>
        <Form.Group widths="2">
          <Form.Input
            required
            label="Email"
            error={formErrors.email}
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleEmailBlur}
          />
        </Form.Group>
        {user ? (
          <>
            <Grid columns="2">
              <Grid.Row>
                <Grid.Column>{userInfoItem("First Name", user.firstName)}</Grid.Column>
                <Grid.Column>{userInfoItem("Last Name", user.lastName)}</Grid.Column>
              </Grid.Row>
            </Grid>
            <br></br>
          </>
        ) : (
          <>
            <Form.Group widths="equal">
              <Form.Input
                label="First Name"
                name="firstName"
                value={formData.firstName}
                error={formErrors.firstName}
                onChange={handleChange}
                required
              />
              <Form.Input
                label="Last name"
                name="lastName"
                value={formData.lastName}
                error={formErrors.lastName}
                onChange={handleChange}
                required
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                required
                label="Password"
                error={formErrors.password}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              <Form.Input
                required
                label="Repeat Password"
                error={formErrors.repeatPassword}
                type="password"
                name="repeatPassword"
                value={formData.repeatPassword}
                onChange={handleChange}
              />
            </Form.Group>
          </>
        )}

        <Form.Group widths="equal">
          <Form.Input label="Phone Number" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
          <Form.Field
            error={formErrors.adminRoleId}
            control={Dropdown}
            selection
            search
            name="adminRoleId"
            value={formData.adminRoleId}
            fluid
            label="Admin Role"
            options={[
              { key: 0, text: "No role", value: null },
              ...(adminRoles?.map((r) => ({
                key: r.id,
                text: r.name.replace(/^[^_]*_/, ""),
                value: r.id,
              })) ?? []),
            ]}
            onChange={handleChange}
            disabled={adminRoleSelectorDisabled}
          />
        </Form.Group>
        <Segment>
          <Label attached="top">Licence</Label>
          <Form.Group widths="equal">
            <Form.Field
              error={formErrors.licenceId}
              control={Dropdown}
              selection
              search
              name="licenceId"
              value={formData.licenceId}
              fluid
              label="Assigned licence"
              options={[
                { key: 0, text: "No licence", value: null },
                ...(activeLicences?.map((l) => ({
                  key: l.id,
                  text: `${l.productName}, Available slots: ${l.availableUserSlots}/${l.numberOfUsers}`,
                  value: l.id,
                })) ?? []),
              ]}
              onChange={handleChange}
              disabled={licenceSelectorDisabled}
            />
          </Form.Group>
        </Segment>
      </Form>
      <br />
      <Tab panes={panes} />
      <ErrorsView errors={errors} />
    </>
  );
};

export default OrgUserAddForm;
